/**
 * Validated Optimization Recommendation Form
 * Production-ready form for AI recommendations with safety/compliance signals
 */

import { Alert, Badge, Button, Card, Group, Paper, Select, Stack, Text, Textarea, TextInput, Title } from '@mantine/core';
import { IconAlertTriangle, IconCheck, IconRobot } from '@tabler/icons-react';
import React, { useMemo } from 'react';
import { useValidatedForm, useValidatedFormSubmission } from '../hooks/useValidatedForm';
import { OPTIMIZATION_RECOMMENDATION_TYPES, OptimizationRecommendation, RECOMMENDATION_SEVERITIES, SAFETY_THRESHOLDS } from '../types-16-complete';

interface ValidatedOptimizationRecommendationFormProps {
    initialData?: Partial<OptimizationRecommendation>;
    mode: 'create' | 'edit';
    onSuccess?: (data: OptimizationRecommendation) => void;
    onCancel?: () => void;
    /** P0 SECURITY: Enable when form is properly wired through EnhancedComponentProvider with security boundaries */
    securityBoundaryEnabled?: boolean;
    /** Optional GraphQL mutation function for secure submission */
    mutationFunction?: (data: any) => Promise<{ success: boolean; data?: any; error?: string }>;
}

export const ValidatedOptimizationRecommendationForm: React.FC<ValidatedOptimizationRecommendationFormProps> = ({
    initialData,
    mode,
    onSuccess,
    onCancel,
    securityBoundaryEnabled = false,
    mutationFunction
}) => {
    const initialValues = useMemo(() => ({
        id: initialData?.id || '',
        workflow_name: initialData?.workflow_name || '',
        tenant: initialData?.tenant || '',
        recommendation_type: initialData?.recommendation_type || OPTIMIZATION_RECOMMENDATION_TYPES.PERFORMANCE,
        severity: initialData?.severity || RECOMMENDATION_SEVERITIES.MEDIUM,
        priority_score: initialData?.priority_score ?? 50,
        description: initialData?.description || '',
        root_cause_analysis: initialData?.root_cause_analysis || '',
        estimated_impact: initialData?.estimated_impact || {
            performance_improvement: { execution_time_reduction_pct: 0, throughput_increase_pct: 0, error_rate_reduction_pct: 0 },
            cost_impact: { annual_savings_usd: 0, implementation_cost_usd: 0, roi_months: 0 },
            reliability_improvement: { availability_increase_pct: 0, mttr_reduction_pct: 0, sla_compliance_improvement_pct: 0 }
        },
        safety_validation: initialData?.safety_validation || {
            patient_safety_check: false,
            financial_integrity_check: true,
            legal_privilege_check: true,
            emergency_procedure_impact: false,
            safety_score: SAFETY_THRESHOLDS.MINIMUM_SAFETY_SCORE,
            safety_blockers: []
        },
        status: initialData?.status || 'PENDING',
        metadata: initialData?.metadata || {}
    }), [initialData]);

    const form = useValidatedForm<any>({
        schemaName: 'OptimizationRecommendation' as any,
        initialValues,
        validateOnChange: true,
        validateOnBlur: true,
        showNotifications: false
    });

    const { submitForm, loading: submitting, submissionResult } = useValidatedFormSubmission(
        'OptimizationRecommendation' as any,
        () => {
            if (!securityBoundaryEnabled || !mutationFunction) {
                // P0 SECURITY: Block submission if no real persistence is wired
                // Life-critical systems must not accept actions that appear successful but don't persist
                throw new Error('SECURITY_BOUNDARY_VIOLATION: ValidatedOptimizationRecommendationForm requires proper GraphQL mutation wiring via EnhancedComponentProvider. Cannot submit without validated backend persistence.');
            }
            return {
                mutate: mutationFunction,
                loading: false,
                error: null
            };
        }
    );

    const handleSubmit = async (values: any) => {
        const result = await submitForm(values);
        if (result.success && onSuccess) onSuccess(result.data as OptimizationRecommendation);
    };

    return (
        <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack gap="lg">
                {!securityBoundaryEnabled && (
                    <Alert color="red" title="SECURITY BOUNDARY REQUIRED" icon={<IconAlertTriangle />} style={{ border: '2px solid red' }}>
                        <Text size="sm" fw={600}>
                            This form is NOT wired for life-critical operation. It requires:
                        </Text>
                        <Text size="sm" component="ul" style={{ marginTop: 8 }}>
                            <li>EnhancedComponentProvider with security boundary</li>
                            <li>Validated GraphQL mutation function</li>
                            <li>Audit trail integration</li>
                        </Text>
                        <Text size="sm" style={{ marginTop: 8 }}>
                            Submit button will be disabled until proper security context is provided.
                        </Text>
                    </Alert>
                )}
                <Paper p="md" withBorder>
                    <Group justify="space-between">
                        <Group>
                            <IconRobot size={24} />
                            <Title order={3}>{mode === 'create' ? 'Create' : 'Edit'} Optimization Recommendation</Title>
                        </Group>
                        <Badge color={form.validationResult.valid ? 'green' : 'red'} leftSection={form.validationResult.valid ? <IconCheck size={14} /> : <IconAlertTriangle size={14} />}>
                            {form.validationResult.valid ? 'Valid' : `${form.validationResult.errors.length} error(s)`}
                        </Badge>
                    </Group>
                </Paper>

                {form.validationResult.errors.length > 0 && (
                    <Alert color="red" title="Validation Errors" icon={<IconAlertTriangle />}> 
                        {form.validationResult.errors.map((e, i) => (
                            <Text key={i} size="sm"><strong>{e.field}:</strong> {e.message}</Text>
                        ))}
                    </Alert>
                )}

                <Card withBorder>
                    <Stack>
                        <Group grow>
                            <TextInput label="Workflow Name" required {...form.getInputProps('workflow_name')} />
                            <TextInput label="Tenant" required {...form.getInputProps('tenant')} />
                        </Group>
                        <Group grow>
                            <Select
                                label="Recommendation Type"
                                data={[
                                    { value: 'PERFORMANCE', label: 'Performance' },
                                    { value: 'COST', label: 'Cost' },
                                    { value: 'RELIABILITY', label: 'Reliability' },
                                    { value: 'COMPLIANCE', label: 'Compliance' },
                                    { value: 'SAFETY', label: 'Safety' }
                                ]}
                                {...form.getInputProps('recommendation_type')}
                            />
                            <Select
                                label="Severity"
                                data={[
                                    { value: 'LOW', label: 'Low' },
                                    { value: 'MEDIUM', label: 'Medium' },
                                    { value: 'HIGH', label: 'High' },
                                    { value: 'CRITICAL', label: 'Critical' }
                                ]}
                                {...form.getInputProps('severity')}
                            />
                        </Group>
                        <Textarea label="Description" minRows={3} {...form.getInputProps('description')} />
                        <Textarea label="Root Cause Analysis" minRows={3} {...form.getInputProps('root_cause_analysis')} />
                    </Stack>
                </Card>

                <Paper p="md" withBorder>
                    <Group justify="space-between">
                        <Button variant="light" onClick={onCancel}>Cancel</Button>
                        <Button
                            type="submit"
                            loading={submitting || form.isValidating}
                            disabled={!form.validationResult.valid || !securityBoundaryEnabled}
                            color={securityBoundaryEnabled ? undefined : 'red'}
                        >
                            {!securityBoundaryEnabled ? 'SECURITY REQUIRED' : mode === 'create' ? 'Create Recommendation' : 'Update Recommendation'}
                        </Button>
                    </Group>
                </Paper>

                {submissionResult && (
                    <Alert color={submissionResult.success ? 'green' : 'red'} title={submissionResult.success ? 'Success' : 'Error'}>
                        {submissionResult.success ? 'Saved successfully' : submissionResult.error}
                    </Alert>
                )}
            </Stack>
        </form>
    );
};

export default ValidatedOptimizationRecommendationForm;


